/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { useManageIPMSContext } from "./ManageIPMSContex";
import { GoalAccordion } from "./goal";
import { getQuarterRanges } from "./convertionHealper";
import { QuarterDates } from "../../model/ipmsmodel";
import { GetEmployeeGoalDataResponse } from "./newipmsmodel";

export interface GoalModalState {
  employeeID: number;
  employeeName: string;
  /** Assessment year start date (YYYY-MM-DD) */
  start: string;
  /** Assessment year end date (YYYY-MM-DD) */
  end: string;
  quarterName: string;
}

interface GoalModalProps {
  modalState: GoalModalState | null;
  onClose: () => void;
}

const GoalModal: React.FC<GoalModalProps> = ({ modalState, onClose }) => {
  const { GetEmployeeGoal, TSApproverList } = useManageIPMSContext();

  // Pre-compute quarter date ranges from start/end (same logic as GoalPage)
  const calculatedQuarters = React.useMemo<QuarterDates | null>(() => {
    if (!modalState?.start || !modalState?.end) return null;
    const start = moment(modalState.start);
    const end = moment(modalState.end);
    const ranges = getQuarterRanges(start, end);
    const quarterList: Record<string, { start: string; end: string }> = {};
    ranges.forEach((r, i) => {
      quarterList[`q${i + 1}`] = {
        start: r.start?.format("YYYY-MM-DD") || "",
        end: r.end?.format("YYYY-MM-DD") || "",
      };
    });
    return quarterList as unknown as QuarterDates;
  }, [modalState?.start, modalState?.end]);

  const { data: employeeGoalData, isLoading } = useQuery({
    queryKey: ["goalModal", modalState?.employeeID, modalState?.start],
    queryFn: async () => {
      if (!modalState?.employeeID || !modalState?.start) return null;
      const response: any = await GetEmployeeGoal({
        employee_id: modalState.employeeID,
        start_assessment_date: modalState.start,
      });
      return response ?? null;
    },
    enabled: !!modalState?.employeeID && !!modalState?.start,
  });

  // Close on Escape key
  React.useEffect(() => {
    if (!modalState) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [modalState, onClose]);

  if (!modalState) return null;

  return (
    // Overlay — starts below the app navbar
    <div
      className="fixed top-[70px] inset-x-0 bottom-0 z-50 flex flex-col bg-white overflow-hidden"
      role="dialog"
      aria-modal="true"
    >
      {/* Sticky header */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200 shadow-sm z-50">
        <div>
          <p className="text-sm font-semibold text-gray-800">
            Employee:{" "}
            <span className="text-blue-600">{modalState.employeeName}</span>
            {" · "}ID: {modalState.employeeID}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Assessment Period:{" "}
            {moment(modalState.start).format("DD/MM/YYYY")} –{" "}
            {moment(modalState.end).format("DD/MM/YYYY")}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
          title="Close (Esc)"
        >
          <XMarkIcon className="w-4 h-4" />
          Close
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto container mx-auto px-4 pb-6">
        {isLoading && (
          <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
            Loading goals…
          </div>
        )}

        {!isLoading && !employeeGoalData && (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
            No goal data found for this employee.
          </div>
        )}

        {!isLoading && employeeGoalData && calculatedQuarters && (
          <GoalAccordion
            data={employeeGoalData as unknown as GetEmployeeGoalDataResponse}
            getQuatersList={calculatedQuarters}
            assessmentStart={modalState.start}
            assessmentEnd={modalState.end}
            routestate={""}
            formType="ViewOnly"
            startyear={modalState.start}
            employeeID={modalState.employeeID}
            approverList={TSApproverList ?? []}
            isViewOnly={true}
          />
        )}
      </div>
    </div>
  );
};

export default GoalModal;
